import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Mountain, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetTier: 'adventurer' | 'organizer';
  featureContext?: string;
}

const tierConfig = {
  adventurer: {
    name: "Adventurer",
    icon: Mountain,
    price: "$29.99/year",
    monthlyPrice: "$4.99/mo",
    color: "text-primary",
    description: "Unlock the full trail experience",
    benefits: [
      "View community run details, photos & waypoints",
      "Trail segment difficulty feedback & sentiment",
      "Control run privacy (public/private)",
      "Edit and clean run tracks",
      "Enhanced dashboard insights",
      "Personalized trail recommendations"
    ]
  },
  organizer: {
    name: "Organizer",
    icon: Users,
    price: "$59.99/year",
    monthlyPrice: "$8.99/mo",
    color: "text-accent",
    description: "Lead the pack and build your community",
    benefits: [
      "Everything in Adventurer",
      "Create and host events (public or private)",
      "Event waiver form generation",
      "Create and export routes (GPX)",
      "Organizer analytics dashboard",
      "Priority support"
    ]
  }
};

const UpgradeDialog = ({
  open,
  onOpenChange,
  targetTier,
  featureContext,
}: UpgradeDialogProps) => {
  const navigate = useNavigate();
  const config = tierConfig[targetTier];
  const Icon = config.icon;

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/pricing');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className={`w-8 h-8 ${config.color}`} />
          </div>
          <DialogTitle className="text-2xl">
            Upgrade to {config.name}
          </DialogTitle>
          <DialogDescription className="text-base">
            {featureContext 
              ? `Upgrade to ${config.name} to ${featureContext}`
              : config.description
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="text-center mb-4">
            <span className="text-3xl font-bold">{config.price}</span>
            <span className="text-muted-foreground text-sm ml-2">
              or {config.monthlyPrice}
            </span>
          </div>

          <ul className="space-y-2">
            {config.benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            variant="hero" 
            className="w-full" 
            onClick={handleUpgrade}
          >
            View Plans & Upgrade
          </Button>
          <Button 
            variant="ghost" 
            className="w-full" 
            onClick={() => onOpenChange(false)}
          >
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeDialog;
